import React from 'react'
import { observer } from 'mobx-react'
import { BetterComponent } from '../better-component/better-component'
import { Tooltip } from 'antd'
import { cleanNumericDisplays } from '../../_utils/utils'
import { getCurrencyFormatterForCode } from '../../_utils/domain'
import numeral from 'numeral'

interface IProps {
    /** Attributed purchase amount in the domain's own currency. */
    amount: number
    /** ISO 4217 code resolved from the domain — one currency per domain. */
    currencyCode: string
}

interface IState {}

/** Shown wherever there is no amount worth putting a currency figure on. */
const NO_AMOUNT_PLACEHOLDER = '--'

/**
 * Splits numeral's abbreviated output into the number it shortened to and the
 * magnitude suffix it chose (`-12.35k` into `-12.35` and `k`), so the number can
 * be re-formatted as currency while keeping numeral's rounding and its choice of
 * suffix.
 */
const ABBREVIATED_NUMBER_PATTERN = /^(-?[\d.]+)([a-z]*)$/

/**
 * Single-line currency cell for attributed revenue. Zero renders as the same
 * placeholder the purchase metrics use for "nothing to report", so a zero and an
 * absent figure read alike across the whole purchase column group.
 */
@observer
export class RevenueColumnView extends BetterComponent<IProps, IState> {
    public render() {
        const { amount, currencyCode } = this.props
        if (!amount) {
            return (
                <div className="revenue-col-view">
                    <span className="revenue-value">{NO_AMOUNT_PLACEHOLDER}</span>
                </div>
            )
        }

        const currencyFormatter = getCurrencyFormatterForCode(currencyCode)
        const fullValue = currencyFormatter.format(amount)
        const value = this.renderAmount(amount, currencyFormatter)

        return (
            <div className="revenue-col-view">
                {value === fullValue ? (
                    <span className="revenue-value">{value}</span>
                ) : (
                    <Tooltip trigger={'click'} title={fullValue}>
                        <span className="revenue-value cursor-pointer">{value}</span>
                    </Tooltip>
                )}
            </div>
        )
    }

    /**
     * Amounts of five whole digits or more are abbreviated so they fit the
     * column. Both the abbreviated and the full-precision figure go through the
     * same currency formatter, so every row of the column agrees on where the
     * symbol sits, how a negative amount is signed, and how many minor-unit
     * digits the currency has. Pasting a bare symbol in front of an abbreviated
     * number gets all three wrong for some currencies.
     */
    private renderAmount(amount: number, currencyFormatter: Intl.NumberFormat): string {
        const wholeDigitCount = String(Math.trunc(Math.abs(amount))).length
        if (wholeDigitCount <= 4) return currencyFormatter.format(amount)

        const abbreviated = ABBREVIATED_NUMBER_PATTERN.exec(numeral(amount).format('0.00a'))
        if (!abbreviated) return currencyFormatter.format(amount)

        const [, shortenedAmount, magnitudeSuffix] = abbreviated

        return cleanNumericDisplays(`${currencyFormatter.format(Number(shortenedAmount))}${magnitudeSuffix}`)
    }
}
